const generalTerms = [
  {
    title: 'Limited access, not ownership',
    body: 'Buying or receiving a reference pack gives the user a limited, non-exclusive, non-transferable, revocable license to view and use the included photos, videos, stills, and metadata for the permitted reference uses. The model, seller, photographer, host, or platform retain all rights not expressly granted.',
  },
  {
    title: 'The artist owns their own artwork',
    body: 'Artists own the original drawings, paintings, studies, sculptures, and other human-made artworks they create from a reference pack. That ownership does not include ownership of the source photos, videos, recordings, model likeness, or any right to redistribute the pack itself.',
  },
  {
    title: 'Access can always be withdrawn',
    body: 'A model may ask LifeDrawing.live to remove or withdraw access to a reference pack at any time. LifeDrawing.live may also withdraw, restrict, or suspend access for safety, privacy, consent, legal, payment, or policy reasons. After withdrawal, users must stop accessing the pack, stop making new works from it, and delete any downloaded source files. Ordinary completed artworks made before withdrawal remain allowed unless the user breached these terms or used the pack in a harmful, unlawful, or prohibited way.',
  },
  {
    title: 'No source redistribution',
    body: 'Users may not sell, publish, upload, share, sublicense, lend, scrape, mirror, stock-list, or otherwise redistribute source files, previews, screenshots, extracted frames, download links, or substantially identical copies of the reference pack.',
  },
  {
    title: 'Model dignity and privacy',
    body: 'Users must not use a model reference pack for harassment, impersonation, deepfakes, biometric identification, face recognition datasets, explicit sexual or pornographic material, defamatory material, hate material, political endorsement, medical endorsement, product endorsement, or any use that suggests the model personally approves of a message, product, service, or cause.',
  },
  {
    title: 'No AI or machine learning training',
    body: 'Reference packs may not be used to train, fine-tune, evaluate, prompt, benchmark, or build an AI model or dataset, including image generators, pose generators, recognition systems, or style/likeness models.',
  },
  {
    title: 'Names and attribution',
    body: 'Unless the pack page says otherwise, users should not publish the model’s real name, contact details, location, or private information. Artists may credit the model only when the model has made that credit public or given permission.',
  },
];

const licenseTerms = [
  {
    id: 'personal_reference',
    title: 'Artist Reference License',
    summary: 'For an individual artist using the pack as a private drawing, painting, sculpture, or study reference.',
    permitted: [
      'View the pack during the access period.',
      'Create original human-made artworks from the pack.',
      'Exhibit, post, portfolio, sell, or license those finished artworks, provided the source files are not redistributed and the use does not imply model endorsement.',
      'Use finished artworks in ordinary artist promotion, such as a portfolio page, exhibition listing, social post, or commission announcement.',
    ],
    notPermitted: [
      'Share the pack with another person or account.',
      'Use the pack as teaching material for a class, workshop, livestream, tutorial, or group session.',
      'Use source images or near-source copies as stock, merchandise, advertising, packaging, or product imagery.',
      'Create works whose primary value is a recognizable commercial likeness of the model rather than the artist’s own expressive work.',
    ],
  },
  {
    id: 'classroom_reference',
    title: 'Classroom Reference License',
    summary: 'For hosts, teachers, and organizers who need to show a pack to students or session guests.',
    permitted: [
      'Use the pack as live or recorded teaching reference during the access period.',
      'Display the pack to enrolled students, ticket holders, or invited session guests in a controlled class or workshop.',
      'Let students create their own human-made artworks from the displayed reference.',
      'Give students access to class recordings only when the pack remains embedded in the teaching context and is not downloadable as source media.',
    ],
    notPermitted: [
      'Give students source files or download links.',
      'Reuse the pack in unrelated courses, subscription libraries, open archives, or public tutorials unless a new license is purchased or permission is given.',
      'Allow students or guests to redistribute screenshots, extracted frames, or recordings of the source material.',
      'Use the pack after access has expired or been withdrawn.',
    ],
  },
];

export function ReferencePackLicenseContent() {
  return (
    <div className="space-y-10">
      <section className="rounded-xl border border-line bg-panel p-6 shadow-sm">
        <div className="page-eyebrow">Reference pack terms</div>
        <h2 className="mt-2 font-display text-2xl font-normal text-text">Reference Pack Access Terms.</h2>
        <p className="mt-4 max-w-3xl text-sm leading-7 text-text-dim">
          These terms form part of the LifeDrawing.live terms and apply whenever a reference pack is sold,
          granted, or distributed through the platform. They are written to protect model privacy and consent
          while giving artists enough certainty to make, show, and sell their own finished artworks.
        </p>
      </section>

      <section>
        <h3 className="font-display text-xl font-normal text-text">Terms that apply to every reference pack.</h3>
        <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
          {generalTerms.map((term) => (
            <article key={term.title} className="rounded-lg border border-line bg-white p-4">
              <h4 className="font-medium text-text">{term.title}</h4>
              <p className="mt-2 text-sm leading-6 text-text-dim">{term.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section>
        <h3 className="font-display text-xl font-normal text-text">License options.</h3>
        <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-2">
          {licenseTerms.map((license) => (
            <article key={license.id} id={license.id} className="scroll-mt-28 rounded-xl border border-line bg-panel p-5 shadow-sm">
              <h4 className="font-display text-[22px] font-normal text-text">{license.title}</h4>
              <p className="mt-2 text-sm leading-6 text-text-dim">{license.summary}</p>

              <div className="mt-5 grid grid-cols-1 gap-4">
                <div>
                  <h5 className="text-xs font-semibold uppercase text-text-dim">Permitted</h5>
                  <ul className="mt-2 space-y-2 text-sm leading-6 text-text-dim">
                    {license.permitted.map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h5 className="text-xs font-semibold uppercase text-text-dim">Not permitted</h5>
                  <ul className="mt-2 space-y-2 text-sm leading-6 text-text-dim">
                    {license.notPermitted.map((item) => (
                      <li key={item} className="flex gap-2">
                        <span className="mt-[0.55em] h-1.5 w-1.5 shrink-0 rounded-full bg-warn" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
